import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, CardHeader, CardBody, Row, Col, Input, FormGroup, Label } from 'reactstrap'
import { Bar } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'
import { store } from '../../stores/reduxStore'

const PROJECTS_QUERY = gql`
  query DailyBurnRateProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query DailyBurnRatePerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsDailyBurnRate($project_id: ID!) {
    stats_daily_burn_rate(project_id: $project_id) {
      project_id project_name required_daily_mm
      daily_data { day mm welders welding ri ndt }
      heatmap { day mm }
    }
  }
`

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const mmToColor = (mm, max) => {
  if (!mm || mm === 0) return '#ebedf0'
  const t = Math.log1p(mm) / Math.log1p(max)
  if (t < 0.25) return '#9be9a8'
  if (t < 0.5)  return '#40c463'
  if (t < 0.75) return '#30a14e'
  return '#216e39'
}

const RANGE_OPTIONS = [7, 14, 30, 90, 180]

class DailyBurnRate extends Component {
  constructor(props) {
    super(props)
    const params = new URLSearchParams(window.location.search)
    this.state = {
      projects: [],
      project_id: params.get('project_id') || '',
      data: null,
      loading: false,
      error: null,
      tooltip: null,
      dailyRange: 90,
      heatmapRange: 365
    }
  }

  componentDidMount() {
    const { client } = this.props
    const personId = store.getState().user.data.person_id
    Promise.all([
      client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([pRes, meRes]) => {
      const userCompanyIds = (meRes.data.person.company_ids || []).map(String)
      const allProjects = pRes.data.projects.list
      const projects = allProjects.filter(p => {
        if (!userCompanyIds.length) return true
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      })
      this.setState({ projects }, () => {
        if (this.state.project_id) this.loadData(this.state.project_id)
      })
    })
  }

  loadData(project_id) {
    this.setState({ loading: true, error: null })
    this.props.client.query({
      query: DATA_QUERY,
      variables: { project_id },
      fetchPolicy: 'network-only'
    }).then(({ data }) => {
      this.setState({ data: data.stats_daily_burn_rate[0] || null, loading: false })
    }).catch(e => {
      this.setState({ error: e.message, loading: false })
    })
  }

  handleProjectChange(project_id) {
    this.setState({ project_id, data: null })
    const url = project_id
      ? `${window.location.pathname}?project_id=${project_id}`
      : window.location.pathname
    window.history.replaceState(null, '', url)
    if (project_id) this.loadData(project_id)
  }

  renderHeatmap(heatmap) {
    if (!heatmap || heatmap.length === 0) return null
    const max = Math.max(...heatmap.map(d => d.mm))
    const byDay = {}
    heatmap.forEach(d => { byDay[d.day] = d.mm })

    const start = new Date(heatmap[0].day)
    const end = new Date(heatmap[heatmap.length - 1].day)
    const weeks = []
    const cur = new Date(start)
    cur.setDate(cur.getDate() - cur.getDay())

    while (cur <= end) {
      const week = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(cur)
        const key = d.toISOString().split('T')[0]
        week.push({ date: key, mm: byDay[key] || 0 })
        cur.setDate(cur.getDate() + 1)
      }
      weeks.push(week)
    }

    const { tooltip } = this.state

    return (
      <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative' }}>
          {tooltip && (
            <div style={{
              position: 'fixed', top: tooltip.y - 40, left: tooltip.x - 60,
              background: '#333', color: '#fff', padding: '4px 8px',
              borderRadius: '4px', fontSize: '11px', pointerEvents: 'none', zIndex: 9999, whiteSpace: 'nowrap'
            }}>
              {DAYS[new Date(tooltip.date + 'T12:00:00').getDay()]} {tooltip.date}: {tooltip.logs.toLocaleString()} logs
            </div>
          )}
          <div style={{ display: 'flex', gap: '2px' }}>
            {weeks.map((week, wi) => (
              <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {week.map((day, di) => (
                  <div key={di}
                    onMouseEnter={e => this.setState({ tooltip: { date: day.date, logs: day.mm, x: e.clientX, y: e.clientY } })}
                    onMouseMove={e => this.setState({ tooltip: { date: day.date, logs: day.mm, x: e.clientX, y: e.clientY } })}
                    onMouseLeave={() => this.setState({ tooltip: null })}
                    style={{ width: '12px', height: '12px', backgroundColor: mmToColor(day.mm, max), borderRadius: '2px', cursor: 'default' }} />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-1 text-muted small">
          <span style={{ marginRight: '8px' }}>Less</span>
          {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
            <span key={i} style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: mmToColor(v * max, max), borderRadius: '2px', marginRight: '2px' }} />
          ))}
          <span style={{ marginLeft: '4px' }}>More</span>
        </div>
      </div>
    )
  }

  render() {
    const { projects, project_id, data, loading, error, dailyRange, heatmapRange } = this.state

    const filterByDays = (arr, days) => {
      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - days)
      return arr.filter(d => new Date(d.day) >= cutoff)
    }

    const dailyFiltered = data ? filterByDays(data.daily_data, dailyRange) : []
    const heatmapFiltered = data ? filterByDays(data.heatmap, heatmapRange) : []

    const avgOf = (arr, key) => {
      const active = arr.filter(d => d[key] > 0)
      if (!active.length) return 0
      return Math.round(active.reduce((s, d) => s + d[key], 0) / active.length)
    }

    const dailyChart = data && {
      labels: dailyFiltered.map(d => {
        const date = new Date(d.day)
        return `${DAYS[date.getDay()]} ${d.day.slice(5)}`
      }),
      datasets: [
        {
          label: 'logs count',
          data: dailyFiltered.map(d => d.mm),
          backgroundColor: dailyFiltered.map(d =>
            data.required_daily_mm && d.mm >= data.required_daily_mm
              ? 'rgba(40,167,69,0.7)' : 'rgba(54,162,235,0.5)'
          ),
          borderWidth: 0
        },
        data.required_daily_mm ? {
          label: 'Required daily',
          data: dailyFiltered.map(() => data.required_daily_mm),
          type: 'line',
          borderColor: 'rgba(220,53,69,0.8)',
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0
        } : null
      ].filter(Boolean)
    }

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-fire mr-2" />Daily Burn Rate</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            <FormGroup row className="mb-3">
              <Label sm={2}>Project</Label>
              <Col sm={4}>
                <Input type="select" value={project_id} onChange={e => this.handleProjectChange(e.target.value)}>
                  <option value="">— select project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </Col>
              {project_id && (
                <Col sm={2}>
                  <Input type="select" value={dailyRange} onChange={e => this.setState({ dailyRange: parseInt(e.target.value) })}>
                    {RANGE_OPTIONS.map(d => <option key={d} value={d}>Last {d} days</option>)}
                  </Input>
                </Col>
              )}
            </FormGroup>

            {loading && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}
            {!project_id && !loading && <div className="text-muted">Select a project to view data.</div>}

            {data && (
              <div>

                <Row className="mb-2">
                  {[
                    ['Avg daily logs', avgOf(dailyFiltered, 'mm')],
                    ['Avg weekly logs', Math.round(avgOf(dailyFiltered, 'mm') * 7)],
                    ['Avg monthly logs', Math.round(avgOf(dailyFiltered, 'mm') * 30)]
                  ].map(([label, value]) => (
                    <Col md="3" key={label}>
                      <div className="text-center p-3 border rounded">
                        <div className="text-muted small">{label}</div>
                        <div className="h5 mb-0"><strong>{value.toLocaleString()}</strong></div>
                      </div>
                    </Col>
                  ))}
                </Row>

                {[['Welding', 'welding'], ['RI', 'ri'], ['Inspection (NDT)', 'ndt']].map(([title, key]) => (
                  <Row key={key} className="mb-2">
                    <Col md="12"><small className="text-muted">{title}</small></Col>
                    {[
                      ['Avg daily', avgOf(dailyFiltered, key)],
                      ['Avg weekly', Math.round(avgOf(dailyFiltered, key) * 7)],
                      ['Avg monthly', Math.round(avgOf(dailyFiltered, key) * 30)]
                    ].map(([label, value]) => (
                      <Col md="3" key={label}>
                        <div className="text-center p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="text-muted small">{label}</div>
                          <div className="h6 mb-0"><strong>{value.toLocaleString()}</strong></div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ))}

                <h6 className="mb-1">Daily activity — welding &amp; inspection logs (last {dailyRange} days)</h6>
                <Row>
                  <Col md="12">
                    <Bar data={dailyChart} height={80} options={{
                      maintainAspectRatio: true,
                      legend: { display: data.required_daily_mm != null },
                      scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'logs count' } }] }
                    }} />
                  </Col>
                </Row>

                <h6 className="mt-4 mb-1">Activity heatmap — welding &amp; inspection logs (last 365 days)</h6>
                <Row>
                  <Col md="12">
                    {this.renderHeatmap(data.heatmap)}
                  </Col>
                </Row>

                <div className="mt-4 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                  <strong>How it's calculated:</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    <li><strong>Range selector</strong> — filters all KPI boxes and the daily chart to the selected number of days (7/14/30/90/180)</li>
                    <li><strong>Avg daily</strong> — average logs per active day (days with at least 1 log) in the selected range</li>
                    <li><strong>Avg weekly / monthly</strong> — avg daily × 7 / × 30</li>
                    <li><strong>Welding / RI / Inspection (NDT)</strong> — breakdown by log type; RI = radiographic inspection logs, NDT = all other inspection types</li>
                    <li><strong>Daily chart</strong> — total welding &amp; inspection logs per day for the selected range</li>
                    <li><strong>Heatmap</strong> — welding &amp; inspection logs per calendar day over the last 365 days; color scale: <span style={{color:'#9be9a8'}}>&#9632;</span> low → <span style={{color:'#216e39'}}>&#9632;</span> high (logarithmic), grey = no activity</li>
                  </ul>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(DailyBurnRate)



// WEBPACK FOOTER //
// ./src/components/Report/DailyBurnRate.js